import { BankAccountStatus, DirectDebitStatus, FinanceReceiverStatus, LoanStatus } from "@debitroom-software/types";

import { Repayments } from "@/modules/repayments/types";

export type FinanceReceiverTreatmentFeeStatus =
  | "InProgress"
  | "Completed"
  | "Expired"
  | "Cancelled"
  | "Failed"
  | "Prepared";

export enum FinanceReceiverOnboardingStep {
  Welcome = "Welcome",
  QuoteAndFrameworkAgreement = "QuoteAndFrameworkAgreement",
  Sepa = "Sepa",
  Psd = "Psd",
  TreatmentFee = "TreatmentFee"
}

export enum CreditLimitRequestStep {
  Quote = "Quote",
  Sepa = "Sepa",
  Psd = "Psd",
  Complete = "Complete"
}

export enum SepaContractUpdateStep {
  Sepa = "Sepa",
  Psd = "Psd"
}

export enum SepaContractRetryReason {
  InsufficientLimit = "InsufficientLimit"
}

export enum SepaContractStatus {
  Open = "Open",
  Signed = "Signed",
  Expiring = "Expiring",
  Canceled = "Canceled"
}

export type SepaContract = {
  id: string;
  signUrl: string;
  status: SepaContractStatus;
  iban: string;
  isPrinted: boolean;
  retryReason?: SepaContractRetryReason;
};

export type StepperStep = {
  isActionable: boolean;
  isCompleted: boolean;
};

export type CreditLimitRequestSteps = {
  [CreditLimitRequestStep.Quote]: StepperStep;
  [CreditLimitRequestStep.Sepa]: StepperStep;
  [CreditLimitRequestStep.Psd]: StepperStep;
};

type OnboardingSteps = {
  [FinanceReceiverOnboardingStep.Welcome]: StepperStep;
  [FinanceReceiverOnboardingStep.QuoteAndFrameworkAgreement]: StepperStep;
  [FinanceReceiverOnboardingStep.Sepa]: StepperStep;
  [FinanceReceiverOnboardingStep.Psd]: StepperStep;
  [FinanceReceiverOnboardingStep.TreatmentFee]: StepperStep;
};

export interface FinanceReceiverOverview {
  id: string;
  canUpdateSepa: boolean;
  sepaContractRequest: {
    sepaContract: SepaContract;
    isCompleted: boolean;
    steps: {
      Sepa: StepperStep;
      Psd?: StepperStep;
    };
  } | null;
  revolvingCreditFacility: {
    creditLimit: number;
    withdrawnAmount: number;
    availableAmount: number;
    interestRate: number;
    totalOverdueAmount: number;
    activeLoans: LoanListItem[];
    inactiveLoans: LoanListItem[];
    activeLoansWithAttention: LoanListItem[];
    upcomingDirectDebitCollections: UpcomingDirectDebit[];
    isDisabled: boolean;
    creditLimitRequest: {
      isCompleted: boolean;
      steps: CreditLimitRequestSteps;
      sepaContract: SepaContract;
      financeReceiverCompletedAt?: null | string;
    } | null;
  };
  companyName: string;
  companyUrl: string;
  registrationNumber: string;
  phone: string;
  address: {
    street: string;
    houseNumber: string;
    city: string;
    postalCode: string;
    country: string;
  };
  advisors: {
    partnerId: string;
    partnerName: string;
    id: string;
    name: string;
    phone: string;
    email: string;
  }[];
  status: FinanceReceiverStatus;
  bankAccounts: BankAccount[];
  treatmentFee?: {
    id: string;
    amount: number;
    status?: FinanceReceiverTreatmentFeeStatus;
  };
  onboarding: {
    steps: OnboardingSteps;
    isCompleted: boolean;
    canEnterApp: boolean;
    sepaContract: SepaContract;
  } | null;
}

export interface LoanListItem {
  id: string;
  referenceNumber: string;
  status: LoanStatus;
  startDate?: string;
  collectionDate?: string;
  directDebitsCollectionDate?: string;
  amount: number;
  interestRate: number;
  interestAmount: number;
  durationInDays: number;
  canRequestEarlyRepayment: boolean;
  earlyRepaymentRequestedAt?: string;
  repaidAt?: string;
  extraCostsSpecification?: {
    defaultingSince?: string;
    defaultingDays?: number;
    defaultingAmount?: number;
    penaltyRate?: number;
    penaltyAmount?: number;
    administrationCosts?: number;
    totalAmountToReceive?: number;
  };
  renewalCostsSpecification?: {
    interestAmount: number;
    administrationCosts: number;
    penaltyAmount: number;
    totalAmountToReceive: number;
  };
  directDebits: LoanDirectDebit[];
  repayments: Repayments[];
  canRequestRenewal: boolean;
  hasPendingRenewalRequest: boolean;
  hasApprovedRenewalRequest: boolean;
  renewalId?: string;
  renewalReferenceNumber?: string;
  renewalRequestedAt?: Date;
}

export interface LoanDirectDebit {
  id: string;
  status: DirectDebitStatus;
  referenceNumber: string;
  collectionDate: string;
  amount: number;
}

export interface UpcomingDirectDebit {
  id: string;
  amount: number;
  collectionDate: string;
}

export interface extendLoan {
  reason: string;
}

export interface QuoteAndFrameworkAgreementResponse {
  isCurrentSigningAuthority: boolean;
  hasMultipleSigners: boolean;
  signingCompleted: boolean;
  signingUrl: string;
}

export interface QuoteResponse {
  isCurrentSigningAuthority: boolean;
  hasMultipleSigners: boolean;
  signingCompleted: boolean;
  signingUrl: string;
}

export interface BankAccount {
  id: string;
  iban: string;
  holderName: string;
  isSepa: boolean;
  status: BankAccountStatus;
  connectedAt?: string;
  reauthorizationExpectedAt?: string;
  isAuthorizationExpired: boolean;
  isReauthorizationUpcoming: boolean;
  sepaSignedAt?: string;
}
